import type { CreateReportBody, ObraDetail, ReportType } from '@obras/shared';
import {
  BUNNY_STREAM_EXTENSIONS,
  extensionOf,
  formatBytes,
  formatCurrency,
  isVideoExtension,
  MAX_VIDEO_BYTES,
  parseCurrencyToCents,
  REPORT_TYPE_META,
  REPORT_TYPE_ORDER,
} from '@obras/shared';
import * as Icons from 'lucide-react';
import { Check, ShieldAlert, Upload, X } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';

import { Button } from '../../components/ui/Button.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { ProgressBar } from '../../components/ui/Feedback.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { cn } from '../../lib/cn.js';
import { uploadMedia, uploadVideo, useCreateReport, type UploadedMedia } from './api.js';

/**
 * O que o seletor de arquivo oferece.
 *
 * Imagem e PDF vão para o Storage; os contêineres de vídeo vão para o Bunny
 * Stream. A lista de vídeo é a que a biblioteca transcodifica — oferecer mais
 * do que isso só produziria um envio que falha depois de subir.
 */
const ACEITOS = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  ...BUNNY_STREAM_EXTENSIONS.map((extensao) => `.${extensao}`),
].join(',');

const ICON = { strokeWidth: 1.5 } as const;

/** Resolve o ícone canônico do tipo, definido em design.md §6. */
function TypeIcon({ name, className }: { name: string; className?: string }) {
  const Component = (Icons as unknown as Record<string, Icons.LucideIcon>)[name] ?? Icons.FileText;
  return <Component {...ICON} size={16} className={className} aria-hidden />;
}

const TONE_ACTIVE: Record<string, string> = {
  muted: 'border-muted-foreground bg-muted',
  success: 'border-success bg-success-bg',
  info: 'border-info bg-info-bg',
  danger: 'border-danger bg-danger-bg',
  warning: 'border-warning bg-warning-bg',
};

interface PendingFile {
  readonly localId: string;
  readonly file: File;
  status: 'aguardando' | 'enviando' | 'enviado' | 'erro';
  media?: UploadedMedia;
  error?: string;
}

export function ReportFormModal({
  open,
  onClose,
  obra,
}: {
  open: boolean;
  onClose: () => void;
  obra: ObraDetail;
}) {
  const { user } = useAuth();
  const create = useCreateReport(obra.id);
  const fileInput = useRef<HTMLInputElement>(null);

  const [type, setType] = useState<ReportType>('visita');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visitDate, setVisitDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [movementDate, setMovementDate] = useState('');
  const [amount, setAmount] = useState('');
  const [days, setDays] = useState('');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const meta = REPORT_TYPE_META[type];
  const isNotResponsible = obra.responsibleFiscal != null && obra.responsibleFiscal.id !== user?.id;
  const remainingCents = obra.currentValueCents - obra.paidValueCents - obra.glossValueCents;

  const needsAmount = type === 'medicao' || type === 'aditivo_valor' || type === 'supressao_valor';
  const needsDays = type === 'aditivo_prazo_os' || type === 'aditivo_prazo_contrato';

  const uploading = files.some((item) => item.status === 'enviando');

  function reset(): void {
    setType('visita');
    setTitle('');
    setDescription('');
    setVisitDate(new Date().toISOString().slice(0, 10));
    setMovementDate('');
    setAmount('');
    setDays('');
    setFiles([]);
    setFieldErrors({});
    setFormError(null);
  }

  /** Cada arquivo sobe isolado: uma falha não derruba os demais. */
  async function handleFiles(selected: FileList | null): Promise<void> {
    if (!selected || selected.length === 0) return;

    const pending: PendingFile[] = Array.from(selected).map((file) => ({
      localId: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
      file,
      status: 'aguardando',
    }));

    setFiles((current) => [...current, ...pending]);

    for (const item of pending) {
      setFiles((current) =>
        current.map((entry) =>
          entry.localId === item.localId ? { ...entry, status: 'enviando' } : entry,
        ),
      );

      try {
        const video = isVideoExtension(extensionOf(item.file.name));

        if (video && item.file.size > MAX_VIDEO_BYTES) {
          throw new ApiError(
            'PAYLOAD_TOO_LARGE',
            `Vídeo maior que ${Math.round(MAX_VIDEO_BYTES / (1024 * 1024))} MB.`,
          );
        }

        const media = video ? await uploadVideo(item.file) : await uploadMedia(item.file);
        setFiles((current) =>
          current.map((entry) =>
            entry.localId === item.localId ? { ...entry, status: 'enviado', media } : entry,
          ),
        );
      } catch (error) {
        const message =
          error instanceof ApiError ? error.message : 'Falha ao enviar. Tente novamente.';
        setFiles((current) =>
          current.map((entry) =>
            entry.localId === item.localId ? { ...entry, status: 'erro', error: message } : entry,
          ),
        );
      }
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    const body: CreateReportBody = {
      type,
      title: title.trim(),
      description: description.trim() === '' ? undefined : description,
      visitDate,
      movementDate: type === 'visita' ? undefined : movementDate || undefined,
      // A glosa não recebe valor: o servidor usa o saldo remanescente.
      amountCents: needsAmount ? (parseCurrencyToCents(amount) ?? undefined) : undefined,
      days: needsDays ? Number(days) || undefined : undefined,
      mediaIds: files
        .filter((item) => item.status === 'enviado' && item.media)
        .map((item) => item.media!.id),
      status: 'publicado',
    };

    try {
      await create.mutateAsync(body);
      reset();
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors());
        setFormError(error.details ? 'Verifique os campos destacados.' : error.message);
      } else {
        setFormError('Não foi possível salvar o relatório.');
      }
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Novo relatório"
      description="Registre o que foi verificado na visita técnica."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            form="report-form"
            type="submit"
            loading={create.isPending}
            disabled={uploading}
            icon={<Check {...ICON} size={16} />}
          >
            {uploading ? 'Enviando arquivos…' : 'Criar relatório'}
          </Button>
        </>
      }
    >
      <form
        id="report-form"
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-5"
        noValidate
      >
        {isNotResponsible ? (
          <div className="rounded-lg bg-primary-bg p-4">
            <p className="flex items-center gap-2 font-semibold text-primary-strong">
              <ShieldAlert {...ICON} size={16} aria-hidden />
              Você não é o fiscal responsável desta obra
            </p>
            <p className="mt-1 text-[13px] text-primary-strong">
              O relatório será registrado em substituição a {obra.responsibleFiscal?.name}.
            </p>
          </div>
        ) : null}

        {formError ? (
          <div role="alert" className="rounded-md bg-danger-bg px-3.5 py-3 text-[13px] text-danger">
            {formError}
          </div>
        ) : null}

        <fieldset>
          <legend className="text-xs font-semibold text-muted-foreground">Tipo de registro</legend>
          <p className="mt-0.5 mb-3 text-[13px] text-muted-foreground">
            Cada relatório registra um único tipo de movimentação.
          </p>

          {/* Ordem fixa definida pelo cliente — REPORT_TYPE_ORDER. */}
          <div className="grid gap-2 sm:grid-cols-3">
            {REPORT_TYPE_ORDER.map((option) => {
              const optionMeta = REPORT_TYPE_META[option];
              const active = type === option;

              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => setType(option)}
                  aria-pressed={active}
                  className={cn(
                    'flex flex-col gap-1 rounded-md border p-3 text-left transition-colors duration-100',
                    active
                      ? (TONE_ACTIVE[optionMeta.tone] ?? 'border-primary bg-primary-bg')
                      : 'border-border bg-card hover:border-border',
                  )}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <TypeIcon name={optionMeta.icon} />
                    {optionMeta.label}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{optionMeta.hint}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        {needsAmount ? (
          <div className="rounded-lg bg-muted p-4">
            <h3 className="mb-3 text-xs font-semibold text-muted-foreground">
              {type === 'medicao' ? 'Detalhes da medição' : 'Detalhes da movimentação'}
            </h3>

            <TextField
              label="Valor"
              mono
              inputMode="decimal"
              placeholder="R$ 0,00"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              onBlur={(event) => {
                const cents = parseCurrencyToCents(event.target.value);
                if (cents !== null) setAmount(formatCurrency(cents));
              }}
              hint={
                type === 'medicao'
                  ? `Saldo disponível: ${formatCurrency(remainingCents)}`
                  : undefined
              }
              {...(fieldErrors['amountCents'] ? { error: fieldErrors['amountCents'] } : {})}
            />
          </div>
        ) : null}

        {needsDays ? (
          <div className="rounded-lg bg-muted p-4">
            <h3 className="mb-3 text-xs font-semibold text-muted-foreground">
              {type === 'aditivo_prazo_contrato'
                ? 'Aditivo de prazo de contrato'
                : 'Aditivo de prazo da OS'}
            </h3>

            <TextField
              label="Quantidade de dias"
              type="number"
              min={1}
              max={3650}
              mono
              placeholder="0"
              value={days}
              onChange={(event) => setDays(event.target.value)}
              hint={
                type === 'aditivo_prazo_contrato'
                  ? `Fim atual do contrato: ${obra.contractEnd ?? 'não definido'}`
                  : `Fim atual da OS: ${obra.osEnd ?? 'não definido'}`
              }
              {...(fieldErrors['days'] ? { error: fieldErrors['days'] } : {})}
            />
          </div>
        ) : null}

        {type === 'glosa' ? (
          <div className="rounded-lg bg-warning-bg p-4">
            <h3 className="text-xs font-semibold text-warning">Detalhes da glosa</h3>
            <p className="mt-1 text-[13px] text-muted-foreground">
              O valor é o saldo restante a pagar e não pode ser alterado.
            </p>

            <p className="tabular mt-3 text-2xl font-semibold">{formatCurrency(remainingCents)}</p>

            <div className="mt-3">
              <ProgressBar
                value={obra.currentValueCents > 0 ? remainingCents / obra.currentValueCents : 0}
                label="Saldo a glosar"
              />
            </div>
            <p className="mt-2 text-[13px] text-warning">
              Esta glosa encerra o contrato financeiramente.
            </p>
          </div>
        ) : null}

        <TextField
          label="Título"
          required
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          {...(fieldErrors['title'] ? { error: fieldErrors['title'] } : {})}
        />

        <TextAreaField
          label="Descrição"
          rows={6}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          {...(fieldErrors['description'] ? { error: fieldErrors['description'] } : {})}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Data da visita"
            type="date"
            mono
            required
            value={visitDate}
            onChange={(event) => setVisitDate(event.target.value)}
            {...(fieldErrors['visitDate'] ? { error: fieldErrors['visitDate'] } : {})}
          />

          {type !== 'visita' ? (
            <TextField
              label="Data da movimentação"
              type="date"
              mono
              required
              hint="Data do ato administrativo, que pode diferir da visita."
              value={movementDate}
              onChange={(event) => setMovementDate(event.target.value)}
              {...(fieldErrors['movementDate'] ? { error: fieldErrors['movementDate'] } : {})}
            />
          ) : null}
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">
              Fotos, vídeos e documentos
            </span>
            <Button
              size="sm"
              icon={<Upload {...ICON} size={14} />}
              onClick={() => fileInput.current?.click()}
            >
              Adicionar
            </Button>
          </div>

          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACEITOS}
            className="hidden"
            onChange={(event) => {
              void handleFiles(event.target.files);
              event.target.value = '';
            }}
          />

          {files.length === 0 ? (
            <p className="rounded-md bg-muted px-3 py-6 text-center text-[13px] text-muted-foreground">
              Nenhum arquivo. Você também pode anexar depois de publicar.
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {files.map((item) => (
                <li
                  key={item.localId}
                  className="flex items-center gap-3 rounded-md bg-muted px-3 py-2"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium">{item.file.name}</span>
                    <span className="block font-mono text-[11px] text-muted-foreground">
                      {formatBytes(item.file.size)}
                      {item.status === 'enviando' ? ' · enviando…' : ''}
                      {item.status === 'enviado' && item.media?.deduplicated
                        ? ' · já existia no acervo'
                        : ''}
                      {item.status === 'enviado' && item.media?.status === 'processando'
                        ? ' · vídeo em processamento'
                        : ''}
                      {item.status === 'erro' ? ` · ${item.error}` : ''}
                    </span>
                  </span>

                  {item.status === 'enviado' ? (
                    <Icons.CircleCheck {...ICON} size={16} className="text-success" aria-hidden />
                  ) : null}
                  {item.status === 'erro' ? (
                    <Icons.CircleAlert {...ICON} size={16} className="text-danger" aria-hidden />
                  ) : null}
                  {item.status === 'enviando' ? (
                    <Icons.Loader2
                      {...ICON}
                      size={16}
                      className="animate-spin text-muted-foreground"
                      aria-hidden
                    />
                  ) : null}

                  <button
                    type="button"
                    aria-label={`Remover ${item.file.name}`}
                    onClick={() =>
                      setFiles((current) =>
                        current.filter((entry) => entry.localId !== item.localId),
                      )
                    }
                    className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X {...ICON} size={14} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="text-[13px] text-muted-foreground">
          Registro do tipo <strong className="font-semibold">{meta.label}</strong>.{' '}
          {meta.effect === 'none'
            ? 'Não altera valores nem prazos da obra.'
            : 'Ao criar, a movimentação entra no livro-razão da obra.'}
        </p>
      </form>
    </Modal>
  );
}
